import { ComponentFactoryResolver, Directive, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import * as get from 'lodash/get';
import * as isEqual from 'lodash/isEqual';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { ErrorComponent, LoadingComponent } from '../component';
import { DisplayDataFactoryService } from '../services/display-data-factory.service';
import { ViewDataFactoryService } from '../services/view-data-factory.service';

/**
 * ViewDataDirective is a structural directive that automates the retrieval and data state handling of the componentData from the Data Layer.
 * This directive will control the display of the component it's attached on and will setup all data layer bindings.
 * The componentData will be made available in the directive context as ComponentData object 
 * in case of a single ID or a map keyed in by individual ComponentIds to the resulting ComponentData
 *
 * @param componentId is either a single string componentId or an array of strings representing multiple component IDs (required)
 * @param ids (optional) override for the data layer ID source for the data extraction from the store
 * @param loading <TemplateRef> (optional) override for the default loading template @see LoadingComponent for the default implementation
 * @param error <TemplateRef> (optional) override for the default error template @see ErrorComponent for the default implementation
 * @param emptyTemplate <TemplateRef> (optional) override for the empty template
 *
* *Samples*
 * Single ID
 ```html
 <pa-holdings-chart *componentId="'holdings.chart'; let result" [data]="result"></pa-holdings-chart>
 ```
 *
 * Multiple Ids:
```html
<pa-historical-performance *componentId="['performance.historical.returns']; let result" [data]="result['performance.historical.returns']"
	 [chartData]="result['performance.historical.returns.chart']"></pa-historical-performance>
```
 * Custom templates
 ```html
 <ng-template [componentId]="'holdings.chart'" let-result [error]="notApplicable" [loading]="wait">
 	 <pa-holdings-chart [data]="result"></pa-holdings-chart>
 </ng-template>

 <ng-template #notApplicable>
	 <em class="not-applicable">n/a</em>
 </ng-template>

 <ng-template #wait>
	Please be patient
 </ng-template>
 ```
 *
 */
@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: '[componentId]'
})
export class ViewDataDirective implements OnChanges, OnDestroy {
	@Input('componentId') private componentId: string | ComponentRequest | Array<string | ComponentRequest>;
	@Input('ids') private ids$?: Observable<string[]>;
	@Input('loading') private loading?: TemplateRef<any>;
	@Input('error') private error?: TemplateRef<any>;
	@Input('emptyTemplate') private emptyTemplate?: TemplateRef<any>;
	private dataSubscription: Subscription;

	private view: 'none' | 'loading' | 'error' | 'normal' = 'none';

	constructor(
		private viewDataFactory: ViewDataFactoryService,
		private displayDataFactoryService: DisplayDataFactoryService,
		private contentSvc: ContentService,
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private componentFactoryResolver: ComponentFactoryResolver
	) { }

	public ngOnDestroy(): void {
		if (this.componentId) {
			this.viewDataFactory.componentDisposed(<any>this.componentId);
		}
		if (this.dataSubscription) {
			this.dataSubscription.unsubscribe();
		}
	}

	public resolveComponentRequests(componentId: string | ComponentRequest | Array<string | ComponentRequest>): ComponentRequest[] {
		return (Array.isArray(componentId) ? componentId : [componentId]).map(component => {
			if (typeof component === 'string') {
				return {
					componentId: component
				};
			} else if (typeof component === 'object' && component['componentId']) {
				return component;
			} else {
				throw new Error(`Please make sure to pass component Id as string or the ComponentRequest object that has a componentId set`);
			}
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.componentId.currentValue) {
			const componentDatas = this.resolveComponentRequests(changes.componentId.currentValue).map(componentReq => {
				this.viewDataFactory.componentUsed(componentReq.componentId);
				const component$ = this.displayDataFactoryService.getDisplayableComponentData(componentReq.componentId, componentReq.ids || this.ids$, this.contentSvc);
				/* NOTE: The code below is necessary because we don't have :
				1) loading state per element in the store and it's done globally for the entire store
				2) we don't have a cheap way to detect if 2 componentData structures are the same - ideally component data should come with a checksum based on the version of data in the store
				*/
				return componentReq.ignoreLoading
					? component$.pipe(
						filter(data => !data.isLoading),
						distinctUntilChanged((x, y) => {
							return isEqual(x, y);
						})
					)
					: component$;
			});

			if (this.dataSubscription) {
				this.dataSubscription.unsubscribe();
			}

			this.dataSubscription = combineLatest(...componentDatas).subscribe(
				data => {
					if (!data.every(comp => !!comp) || data.find(comp => !comp.visible)) {
						// Component should not be visible - comp should always be defined save for glitches with combinne latest
						if (this.view !== 'none') {
							this.viewContainer.clear();
							this.view = 'none';
						}
						// Rendering empty template
						if (this.view === 'none' && this.emptyTemplate) {
							this.viewContainer.clear();
							this.viewContainer.createEmbeddedView(this.emptyTemplate);
						}
					} else if (data.find(comp => comp.isLoading)) {
						// at least one loading warrants the loading state
						if (this.view !== 'loading') {
							this.viewContainer.clear();
							this.view = 'loading';
							if (this.loading) {
								this.viewContainer.createEmbeddedView(this.loading);
							} else {
								const loadingComponentResolver = this.componentFactoryResolver.resolveComponentFactory(LoadingComponent);
								this.viewContainer.createComponent(loadingComponentResolver, 0);
							}
						}
					} else if (data.every(comp => comp.success || get(comp, 'error.hideOnError', false)) && data.some(comp => comp.success)) {
						// -all of the components have to return successfully for the component to render
						// -some component data success, the error ones are hidden on error
						// TODO: Chris to revisit directive logic
						// if (this.view !== 'normal') {
						this.viewContainer.clear();
						this.view = 'normal';
						const context =
							data.length === 1
								? data[0]
								: data.reduce((acc, val) => {
									acc[val.id] = val;
									return acc;
								}, {});

						this.viewContainer.createEmbeddedView(this.templateRef, { $implicit: context });
						// }
					} else {
						// show error otherwise
						const errorComponents = data.filter(comp => comp.error && !comp.error.hideOnError);
						if (errorComponents.length) {
							// show error only when there are components with error
							console.warn(
								'Unknown component state or partial success on components ',
								errorComponents,
								'\n',
								data.map(comp => comp.id + ':' + (comp.success ? 'success' : comp.isLoading ? 'loading' : 'error')).join('\n')
							);
							this.handleError();
						} else {
							// hide the container otherwise
							this.viewContainer.clear();
						}
					}
				},
				err => {
					console.error('DATA: Got error while trying resolve componentData(s)', err);
					this.handleError();
				}
			);
		}
	}

	public handleError(): void {
		if (this.view !== 'error') {
			this.viewContainer.clear();
			this.view = 'error';
			if (this.error) {
				this.viewContainer.createEmbeddedView(this.error);
			} else {
				const errorComponentResolver = this.componentFactoryResolver.resolveComponentFactory(ErrorComponent);
				this.viewContainer.createComponent(errorComponentResolver, 0);
			}
		}
	}
}

export interface ComponentRequest {
	componentId: string;
	ids?: Observable<string[]>;
	ignoreLoading?: boolean;
}
